import {
  Asset,
  Broadcaster,
  Fixture,
  Link,
} from '@graphql/__generated__/graphql';
import { ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { Locale } from '@core/constants/locales.constants';
import dayjs from '@lib/dayjs';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import {
  Id,
  OPTA_STATUSES,
  isOptaMatchStatus,
  OptaMatchStatus,
  HOME_AWAY_MATCH,
  getMatchStatus,
  MATCH_STATUS,
} from '@stadionhq-v3/core';
import { ButtonProps, MonthGroup, Reveal } from '@stadionhq-v3/base-ui';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import { isLink } from '@core/types/typeGuards';
import { Info, ArrowRightLight, ArrowRight } from '@components/icons';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { renameKeyFromTitleToText } from '@utils/objects.utils';
import FixtureCardSlotted, {
  FixtureCardProps,
} from '@components/v3/organisms/fixture-card-slotted';
import BroadcastersWrapper from '@components/organisms/broadcasters-wrapper/broadcastersWrapper.component';
import { assetTransform, linkTransform } from '../atoms';
import { FixtureCtaDictionary } from './getCoreCtaConfig';
import broadcastersWrapperTransform from '../organisms/broadcastersWrapper.transform';

export type LinksFromSiteSettings = {
  hospitalityLink?: CleanContentful<Link>;
  ticketingLink?: CleanContentful<Link>;
};

export type FixtureResultListingTransformDictionary = FixtureCtaDictionary & {
  live: Translations['fixtures']['status']['live'];
  tbcAbbreviated: Translations['fixtures']['status']['tbcAbbreviated'];
  postponedAbbreviated: Translations['fixtures']['status']['postponedAbbreviated'];
  fullTimeAbbreviated: Translations['fixtures']['status']['fullTimeAbbreviated'];
  halfTimeAbbreviated: Translations['fixtures']['status']['halfTimeAbbreviated'];
  abandonedAbbreviated: Translations['fixtures']['status']['abandonedAbbreviated'];
  penalties: Translations['fixtures']['status']['penalties'];
  penaltiesAbbreviated: Translations['fixtures']['status']['penaltiesAbbreviated'];
  cancelledAbbreviated: Translations['fixtures']['status']['cancelledAbbreviated'];
  extraTimeHalfTimeAbbreviated: Translations['fixtures']['status']['extraTimeHalfTimeAbbreviated'];
  aggregateAbbreviated: Translations['fixtures']['scoreLabel']['aggregateAbbreviated'];
  cancelled: Translations['fixtures']['status']['cancelled'];
  abandoned: Translations['fixtures']['status']['abandoned'];
  postponed: Translations['fixtures']['status']['postponed'];
  addToCalendar: Translations['fixtures']['action']['addToCalendar'];
};

const fixtureResultListingTransform = (
  fixtures: (CleanContentful<Fixture & Id> | undefined)[] | undefined,
  locale: Locale,
  direction: 'forward' | 'reverse',
  dictionary: FixtureResultListingTransformDictionary,
  fallbackClubCrest?: CleanContentful<Asset>,
  linksFromSitesettings?: LinksFromSiteSettings
): MonthGroup[] | undefined => {
  if (!fixtures) {
    return [];
  }

  const siteSettingHospitalityLink = linksFromSitesettings?.hospitalityLink;
  const siteSettingTicketingLink = linksFromSitesettings?.ticketingLink;

  const groups = (direction === 'forward' ? fixtures.reverse() : fixtures)
    .reduce((acc: MonthGroup[], fixtureItem) => {
      if (!fixtureItem) return acc;
      const djs = dayjs(locale);
      const date = djs(fixtureItem.kickOffUtc);
      const year = date.year();
      const month = date.month() + 1; // dayjs month is 0-indexed, +1 to make it 1-indexed
      const monthName = date.format('MMMM');

      const getMatchIndicator = () => {
        if (
          !fixtureItem.homeClub?.dataProviderId ||
          !fixtureItem.awayClub?.dataProviderId ||
          !fixtureItem.team?.dataProviderId
        ) {
          return undefined;
        }
        if (
          fixtureItem.team?.dataProviderId ===
          fixtureItem.homeClub?.dataProviderId
        ) {
          return HOME_AWAY_MATCH.HOME;
        }
        if (
          fixtureItem.team?.dataProviderId ===
          fixtureItem.awayClub?.dataProviderId
        ) {
          return HOME_AWAY_MATCH.AWAY;
        }
        return undefined;
      };

      const hospitalityLink =
        fixtureItem?.hospitalityLink || siteSettingHospitalityLink;
      const ticketingLink =
        fixtureItem?.ticketingLink || siteSettingTicketingLink;

      const matchReportLink =
        fixtureItem?.matchReport?.slug &&
        generateFullPathname({
          locale,
          route: templateConfig.navigation.routes.ARTICLE,
          params: { article: fixtureItem?.matchReport?.slug },
        });

      const matchCentreLink =
        fixtureItem.slug &&
        generateFullPathname({
          locale,
          route: templateConfig.navigation.routes.MATCH_CENTRE,
          params: {
            team: fixtureItem?.team?.slug,
            fixture: fixtureItem?.slug,
          },
        });

      const matchCentreCta =
        matchCentreLink &&
        renameKeyFromTitleToText(
          linkTransform(
            {
              url: matchCentreLink,
              title: dictionary.viewMatchCentre,
            },
            locale,
            <ArrowRight />
          )
        );

      const hospitalityCta =
        hospitalityLink?.url &&
        renameKeyFromTitleToText(
          linkTransform(hospitalityLink, locale, <ArrowRightLight />)
        );

      const ticketingCta =
        ticketingLink?.url &&
        renameKeyFromTitleToText(
          linkTransform(ticketingLink, locale, <ArrowRightLight />)
        );

      const matchReportCta =
        matchReportLink &&
        renameKeyFromTitleToText(
          linkTransform(
            {
              url: matchReportLink,
              title: dictionary.viewMatchReport,
            },
            locale,
            <ArrowRightLight />
          )
        );

      const watchLiveCta = undefined;
      const liveAudioCta = undefined;

      // Only show the link to the first video highlight
      const firstVideoHighlight = fixtureItem?.postMatchHighlightVideos?.[0];
      const videoHighlights = firstVideoHighlight
        ? {
            url: generateFullPathname({
              locale,
              route: templateConfig.navigation.routes.VIDEO_DETAIL,
              params: {
                slug: firstVideoHighlight?.slug || '',
              },
            }),
            text: dictionary.viewHighlights,
            iconAfter: <ArrowRightLight />,
          }
        : undefined;

      const additionalCtas =
        fixtureItem?.additionalLinks
          ?.filter(isLink)
          ?.map((link) =>
            renameKeyFromTitleToText(
              linkTransform(link, locale, <ArrowRightLight />)
            )
          )
          .filter(Boolean) ?? [];

      // Add to Calendar data for client-side component
      const addToCalendarData =
        fixtureItem?.slug && fixtureItem?.team?.slug
          ? (() => {
              const startTime = fixtureItem.kickOffUtc ?? undefined;
              const endTime = startTime
                ? new Date(
                    new Date(startTime).getTime() + 2 * 60 * 60 * 1000
                  ).toISOString()
                : undefined;
              const homeTeam = fixtureItem.homeClub?.name;
              const awayTeam = fixtureItem.awayClub?.name;
              const summary =
                homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : undefined;
              return {
                fixtureSlug: fixtureItem.slug,
                teamSlug: fixtureItem.team.slug,
                text: dictionary.addToCalendar,
                startTime,
                endTime,
                summary,
                location: fixtureItem.venue ?? undefined,
              };
            })()
          : undefined;

      const applyButtonStyling = (
        buttons: ButtonProps[] | undefined,
        maxButtons = 4 // Limit to max 4 buttons
      ): ButtonProps[] => {
        const buttonVariants: ButtonProps['variant'][] = [
          'primary',
          'secondary',
          'tertiary',
          'tertiary',
        ];

        return (
          buttons
            ?.filter(Boolean)
            .slice(0, maxButtons)
            .map((button, index) => ({
              ...button,
              variant: buttonVariants[index],
            })) ?? []
        );
      };

      const mappedMatchStatus = getMatchStatus(
        fixtureItem?.matchPeriod as OptaMatchStatus
      );

      const ctaLinks = () => {
        if (mappedMatchStatus === MATCH_STATUS.PRE_MATCH) {
          return applyButtonStyling([
            matchReportCta,
            hospitalityCta,
            ticketingCta,
            watchLiveCta,
            liveAudioCta,
            ...additionalCtas,
          ] as ButtonProps[]);
        }

        if (mappedMatchStatus === MATCH_STATUS.LIVE) {
          return applyButtonStyling([
            watchLiveCta,
            liveAudioCta,
            ...additionalCtas,
          ] as ButtonProps[]);
        }

        if (mappedMatchStatus === MATCH_STATUS.POST_MATCH) {
          return applyButtonStyling([
            matchReportCta,
            videoHighlights,
            ...additionalCtas,
          ] as ButtonProps[]);
        }

        return [];
      };

      const transformedFixture = {
        // TODO: figure out what labels/values to show for the fixture round
        // https://stadion.atlassian.net/browse/V3DS-352?focusedCommentId=46711
        // fixtureRound: {},
        id: fixtureItem?.id || '',
        matchLocation:
          fixtureItem?.venue && fixtureItem?.city
            ? `${fixtureItem?.venue}, ${fixtureItem?.city}`
            : fixtureItem?.venue || fixtureItem?.city || '',
        leagueTitle: fixtureItem?.competition?.name,
        leagueLogo: fixtureItem?.competition?.logo ? (
          <ContentfulLogo
            {...assetTransform(fixtureItem.competition.logo)}
            width={16}
            height={16}
          />
        ) : null,
        // TODO: update once icon for this is available
        // stadiumIdentifierLogo:
        stadiumIdentifierTitle: getMatchIndicator(),
        home: {
          clubName: fixtureItem?.homeClub?.name,
          clubCrest: fixtureItem?.homeClub?.clubCrest ? (
            <ContentfulLogo
              {...fixtureItem?.homeClub?.clubCrest}
              width={80}
              height={80}
            />
          ) : fallbackClubCrest ? (
            <ContentfulLogo
              {...assetTransform(fallbackClubCrest)}
              width={80}
              height={80}
            />
          ) : undefined,
          score: fixtureItem?.matchData?.header?.home?.score,
          aggregateScore: fixtureItem?.matchData?.header?.home?.aggregateScore,
          penaltyScore: fixtureItem?.matchData?.header?.home?.penaltyScore,
          halfTimeScore: fixtureItem?.matchData?.header?.home?.halfTimeScore,
          isClientTeam:
            fixtureItem?.homeClub?.dataProviderId ===
            fixtureItem?.team?.dataProviderId,
        },
        away: {
          clubName: fixtureItem?.awayClub?.name,
          clubCrest: fixtureItem?.awayClub?.clubCrest ? (
            <ContentfulLogo
              {...fixtureItem?.awayClub?.clubCrest}
              width={80}
              height={80}
            />
          ) : fallbackClubCrest ? (
            <ContentfulLogo
              {...assetTransform(fallbackClubCrest)}
              width={80}
              height={80}
            />
          ) : undefined,
          score: fixtureItem?.matchData?.header?.away.score,
          aggregateScore: fixtureItem?.matchData?.header?.away?.aggregateScore,
          penaltyScore: fixtureItem?.matchData?.header?.away?.penaltyScore,
          halfTimeScore: fixtureItem?.matchData?.header?.away?.halfTimeScore,
          isClientTeam:
            fixtureItem?.awayClub?.dataProviderId ===
            fixtureItem?.team?.dataProviderId,
        },
        metaMessagingCopy: fixtureItem?.additionalTextInfo,
        metaMessagingIcon: <Info />,
        broadcasters: (
          <BroadcastersWrapper
            {...broadcastersWrapperTransform(
              locale,
              fixtureItem?.dataProviderId,
              fixtureItem?.broadcasters as Broadcaster[]
            )}
          />
        ),
        matchStatus:
          fixtureItem?.matchPeriod &&
          isOptaMatchStatus(fixtureItem?.matchPeriod)
            ? fixtureItem?.matchPeriod
            : OPTA_STATUSES.UNKNOWN,
        penalties: {
          home: fixtureItem?.matchData?.header?.penalties?.home,
          away: fixtureItem?.matchData?.header?.penalties?.away,
        },
        ctaLinks: ctaLinks(),
        addToCalendarData:
          mappedMatchStatus === MATCH_STATUS.PRE_MATCH
            ? addToCalendarData
            : undefined,
        matchCentreCta: matchCentreCta && {
          ...matchCentreCta,
          variant: 'text-button',
        },
        labels: dictionary,
      };

      const transformedFixtureCard = (
        <Reveal
          key={`fixture-card-${transformedFixture.id}`}
          fadeIn
          delay={(Number(transformedFixture.id) ?? 1) * 100}
        >
          <FixtureCardSlotted
            fixtureProps={transformedFixture as FixtureCardProps}
            locale={locale}
            kickOffUtc={fixtureItem.kickOffUtc}
          />
        </Reveal>
      );

      const foundGroup = acc?.find(
        (group) => group.year === year && group.month === month
      );

      if (foundGroup) {
        foundGroup.items.push(transformedFixtureCard);
      } else {
        acc.push({
          year,
          month,
          monthName,
          items: [transformedFixtureCard],
        });
      }
      return acc;
    }, [])
    .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));

  return direction === 'reverse' ? groups.reverse() : groups;
};

export default fixtureResultListingTransform;
